import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, CheckCheck, Inbox, Sparkles, ArrowRight, X } from 'lucide-react';
import { RiBell } from '@/components/icons/remix';
import { api, getAccessToken, getActiveCompany } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useUI } from '@/store/ui';
import { Skeleton } from '@/components/EmptyState';

interface Notification {
  id: string;
  user_id: string;
  subject: string;
  body?: string;
  link_doctype?: string;
  link_document_id?: string;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
}

interface ListResp { items: Notification[] }
interface CountResp { count: number }

// Ambient agent suggestions ("nudges") — previously a persistent banner under
// the top chrome. Folded into this drawer so the page header stays compact.
interface Nudge {
  id: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  message: string;
  cta_label?: string;
  cta_prompt?: string;
  created_at: string;
}
interface NudgeResp { items: Nudge[] }

/**
 * NotificationsPopover — the bell in the top-right chrome. It surfaces two
 * streams in one drawer:
 *   1. Suggestions — agent nudges (poll /api/agent/v1/nudges/active), each with
 *      a CTA that seeds the Copilot, and dismissible.
 *   2. Notifications — the last 100 system notifications, click to open the
 *      linked document.
 *
 * The badge counts unread notifications + active suggestions. The doctype→URL
 * mapping mirrors the audit middleware's slug→doctype map.
 */

// Doctype → URL slug. Inverse of internal/platform/audit/middleware.go.
const DOCTYPE_TO_URL: Record<string, { module: string; slug: string }> = {
  customer:         { module: '/accounting', slug: 'customers' },
  supplier:         { module: '/accounting', slug: 'suppliers' },
  item:             { module: '/accounting', slug: 'items' },
  account:          { module: '/accounting', slug: 'accounts' },
  tax_template:     { module: '/accounting', slug: 'tax-templates' },
  sales_invoice:    { module: '/accounting', slug: 'sales-invoices' },
  purchase_invoice: { module: '/accounting', slug: 'purchase-invoices' },
  journal_entry:    { module: '/accounting', slug: 'journal-entries' },
  payment_entry:    { module: '/accounting', slug: 'payment-entries' },
  employee:         { module: '/hr',         slug: 'employees' },
  warehouse:        { module: '/stock',      slug: 'warehouses' },
  project:          { module: '/projects',   slug: 'projects' },
  asset:            { module: '/assets',     slug: 'assets' },
};

function detailUrl(doctype?: string, docId?: string): string | null {
  if (!doctype || !docId) return null;
  const m = DOCTYPE_TO_URL[doctype];
  if (!m) return null;
  return `${m.module}/${m.slug}/${docId}`;
}

async function agentFetch<T>(path: string, opts: { method?: string } = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const co = getActiveCompany();
  if (co) headers['X-Company-Id'] = co;
  const r = await fetch(path, { method: opts.method ?? 'GET', headers });
  const t = await r.text();
  if (!r.ok) throw new Error(t || r.statusText);
  return t ? (JSON.parse(t) as T) : ({} as T);
}

function nudgeTone(p: Nudge['priority']): string {
  switch (p) {
    case 'urgent': return 'bg-brand-error/15 text-brand-error';
    case 'high':   return 'bg-warning/15 text-warning';
    case 'low':    return 'bg-surface text-stone';
    default:       return 'bg-accent/15 text-accent';
  }
}

export function NotificationsPopover() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const openCopilotWith = useUI((s) => s.openCopilotWith);

  // Count polls in the background. List only fetches on popover open to avoid
  // pulling 100 rows every 30s.
  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn:  () => api<CountResp>('/notifications/unread-count'),
    refetchInterval: 30_000,
  });
  const { data: listData, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn:  () => api<ListResp>('/notifications'),
    enabled:  open,
  });

  // Nudges poll continuously (cheap) so the badge reflects new suggestions.
  const { data: nudgeData } = useQuery({
    queryKey: ['agent-nudges-active'],
    queryFn:  () => agentFetch<NudgeResp>('/api/agent/v1/nudges/active'),
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api(`/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
  const markAllRead = useMutation({
    mutationFn: () => api('/notifications/mark-all-read', { method: 'POST' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });
  const dismissNudge = useMutation({
    mutationFn: (id: string) => agentFetch(`/api/agent/v1/nudges/${id}/dismiss`, { method: 'POST' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['agent-nudges-active'] }); },
  });

  const unread = countData?.count ?? 0;
  const items = listData?.items ?? [];
  const nudges = nudgeData?.items ?? [];
  const badge = unread + nudges.length;

  function onItemClick(n: Notification) {
    if (!n.is_read) markRead.mutate(n.id);
    const url = detailUrl(n.link_doctype, n.link_document_id);
    if (url) {
      setOpen(false);
      void navigate({ to: url as never });
    }
  }

  function onNudgeCTA(n: Nudge) {
    setOpen(false);
    openCopilotWith(n.cta_prompt ?? n.message);
  }

  const empty = !isLoading && items.length === 0 && nudges.length === 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          aria-label={`Notifications${badge > 0 ? ` (${badge} new)` : ''}`}
          className="relative inline-flex items-center justify-center size-8 rounded-full text-steel hover:bg-surface hover:text-ink transition-colors"
        >
          <RiBell className="size-[18px]" />
          {badge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-brand-error text-canvas text-[10px] font-semibold leading-none">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="w-[380px] max-h-[70vh] rounded-lg border border-hairline bg-canvas shadow-overlay flex flex-col overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-hairline">
            <div className="text-body-sm font-semibold text-ink">
              Notifications {unread > 0 && <span className="text-stone font-normal">· {unread} unread</span>}
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="inline-flex items-center gap-1 text-caption text-stone hover:text-ink disabled:opacity-50"
              >
                <CheckCheck className="size-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Suggestions section — agent nudges */}
            {nudges.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 text-caption font-medium uppercase tracking-wide text-stone flex items-center gap-1.5">
                  <Sparkles className="size-3 text-accent" /> Suggestions
                </div>
                <ul className="divide-y divide-hairline">
                  {nudges.map((n) => (
                    <li key={n.id} className="px-4 py-3 flex gap-3 items-start">
                      <span className={cn('mt-0.5 inline-flex items-center justify-center size-6 rounded-full shrink-0', nudgeTone(n.priority))}>
                        <Sparkles className="size-3" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-body-sm text-ink">{n.message}</p>
                        {n.cta_label && n.cta_prompt && (
                          <button
                            type="button"
                            onClick={() => onNudgeCTA(n)}
                            className="mt-1 inline-flex items-center gap-1 text-caption text-accent hover:underline underline-offset-2"
                          >
                            {n.cta_label} <ArrowRight className="size-3" />
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => dismissNudge.mutate(n.id)}
                        className="shrink-0 text-stone hover:text-ink p-1 -m-1 rounded"
                        aria-label="Dismiss suggestion"
                        title="Dismiss"
                      >
                        <X className="size-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
                {items.length > 0 && (
                  <div className="px-4 pt-3 pb-1 text-caption font-medium uppercase tracking-wide text-stone">
                    Notifications
                  </div>
                )}
              </div>
            )}

            {isLoading && (
              <div className="p-4 space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            )}

            {empty && (
              <div className="px-6 py-10 flex flex-col items-center text-stone">
                <Inbox className="size-6 mb-2" />
                <span className="text-body-sm">You're all caught up.</span>
              </div>
            )}

            {!isLoading && items.length > 0 && (
              <ul className="divide-y divide-hairline">
                {items.map((n) => {
                  const url = detailUrl(n.link_doctype, n.link_document_id);
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => onItemClick(n)}
                        className={cn(
                          'w-full text-left px-4 py-3 hover:bg-surface transition-colors flex gap-3 items-start',
                          !n.is_read && 'bg-accent/[0.03]',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-1.5 size-2 rounded-full shrink-0',
                            n.is_read ? 'bg-stone/30' : 'bg-accent',
                          )}
                          aria-label={n.is_read ? 'Read' : 'Unread'}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className={cn(
                              'text-body-sm truncate',
                              n.is_read ? 'text-charcoal' : 'text-ink font-medium',
                            )}>
                              {n.subject}
                            </span>
                            <span className="text-caption text-stone shrink-0">{relativeTime(n.created_at)}</span>
                          </div>
                          {n.body && (
                            <p className="text-caption text-stone mt-0.5 line-clamp-2 whitespace-pre-wrap">{n.body}</p>
                          )}
                          {url && (
                            <span className="text-caption text-accent mt-0.5 inline-block">Open →</span>
                          )}
                        </div>
                        {!n.is_read && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); markRead.mutate(n.id); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); markRead.mutate(n.id); } }}
                            className="shrink-0 text-stone hover:text-ink p-1 -m-1 rounded cursor-pointer"
                            aria-label="Mark read"
                            title="Mark read"
                          >
                            <Check className="size-3.5" />
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return 'now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.round(hr / 24);
  if (d < 30) return `${d}d`;
  return new Date(iso).toISOString().slice(0, 10);
}
